import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { GetManifestsParams, GetManifestsSortParams } from 'store/manifests/types';
import { SortDirections } from 'utils/tableHelpers';
import { ManifestStatus } from '../types/manifest-statuses';

export type FilterTag = {
    key: ListManifestsParamKeys;
    label: string;
    value: string;
};

export enum ListManifestsParamKeys {
    SORT_DIRECTION = 'sortDirection',
    SORT = 'sort',
    PAGE = 'page',
    SIZE = 'size',
    REFERENCE_ID = 'referenceId',
    OWNER_ID = 'ownerId',
    TEAM_ID = 'teamId',
    MANIFEST_STATUSES = 'manifestStatuses',
    CREATED_BY = 'createdBy',
    QUERY = 'query',
    EXACT_DATE = 'exactDate',
    ARCHIVED = 'archived',
}

const useManifestsFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialOwnerId = searchParams.get(ListManifestsParamKeys.OWNER_ID) ?? undefined;
    const initialQuery = searchParams.get(ListManifestsParamKeys.QUERY) ?? '';
    const initialPage = Number(searchParams.get(ListManifestsParamKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(ListManifestsParamKeys.SIZE)) || 10;
    const initialArchivedParam = searchParams.get(ListManifestsParamKeys.ARCHIVED) || 'false';
    const initialTeamIdParam = searchParams.get(ListManifestsParamKeys.TEAM_ID) || undefined;
    const initialCreatedByParam = searchParams.get(ListManifestsParamKeys.CREATED_BY) ?? undefined;
    const initialArchived = initialArchivedParam === 'true';
    const initialDate = searchParams.get(ListManifestsParamKeys.EXACT_DATE) || undefined;
    const initialSort =
        (searchParams.get(ListManifestsParamKeys.SORT) as GetManifestsSortParams) || GetManifestsSortParams.CREATED_AT;
    const initialSortDir = (searchParams.get(ListManifestsParamKeys.SORT_DIRECTION) as SortDirections) || 'DESC';
    const initialStatuses = (searchParams.getAll(ListManifestsParamKeys.MANIFEST_STATUSES) as ManifestStatus[]) ?? [];

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [archived, setArchived] = useState<boolean | undefined>(initialArchived);
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);
    const [date, setDate] = useState<string | undefined>(initialDate);
    const [ownerId, setOwnerId] = useState<string | undefined>(initialOwnerId);
    const [teamId, setTeamId] = useState<string | undefined>(initialTeamIdParam);
    const [createdBy, setCreatedBy] = useState<string | undefined>(initialCreatedByParam);
    const [manifestStatuses, setManifestStatuses] = useState<ManifestStatus[]>(initialStatuses);
    const [sortState, setSortState] = useState<{
        sort: GetManifestsSortParams | undefined;
        sortDirection: SortDirections | undefined;
    }>({
        sort: initialSort,
        sortDirection: initialSortDir,
    });

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: GetManifestsParams) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            const applyArray = (key: string, values?: (string | number)[] | null) => {
                searchParam.delete(key);
                if (values && values.length) {
                    values.forEach((v) => searchParam.append(key, String(v)));
                }
            };

            apply(ListManifestsParamKeys.QUERY, next.query);
            apply(ListManifestsParamKeys.SORT, next.sort);
            apply(ListManifestsParamKeys.SORT_DIRECTION, next.sortDirection);
            apply(ListManifestsParamKeys.PAGE, next.page?.toString());
            apply(ListManifestsParamKeys.SIZE, next.size?.toString());
            apply(ListManifestsParamKeys.ARCHIVED, next.archived === undefined ? undefined : String(next.archived));
            apply(ListManifestsParamKeys.EXACT_DATE, next.exactDate);
            apply(ListManifestsParamKeys.OWNER_ID, next.ownerId);
            apply(ListManifestsParamKeys.TEAM_ID, next.teamId);
            apply(ListManifestsParamKeys.CREATED_BY, next.createdBy);
            applyArray(ListManifestsParamKeys.MANIFEST_STATUSES, next.manifestStatuses as ManifestStatus[] | undefined);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            archived,
            query: debouncedQuery,
            exactDate: date?.toString(),
            ownerId,
            teamId,
            createdBy,
            manifestStatuses: manifestStatuses?.length ? manifestStatuses : undefined,
        });
    }, [
        archived,
        page,
        ownerId,
        teamId,
        createdBy,
        size,
        sortState.sort,
        sortState.sortDirection,
        debouncedQuery,
        date,
        manifestStatuses,
        writeParams,
    ]);

    const appliedParams = useMemo(
        () => ({
            archived,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            query: debouncedQuery || undefined,
            exactDate: date,
            ownerId,
            teamId,
            manifestStatuses,
            createdBy,
        }),
        [
            archived,
            createdBy,
            sortState.sort,
            sortState.sortDirection,
            page,
            size,
            debouncedQuery,
            date,
            ownerId,
            teamId,
            manifestStatuses,
        ]
    );

    const tags: FilterTag[] = useMemo(() => {
        const list: FilterTag[] = [];

        if (date) {
            list.push({
                key: ListManifestsParamKeys.EXACT_DATE,
                label: 'Date',
                value: moment(date).format('YYYY-MM-DD'),
            });
        }

        if (ownerId) {
            list.push({
                key: ListManifestsParamKeys.OWNER_ID,
                label: 'Assigned to',
                value: ownerId,
            });
        }

        if (teamId) {
            list.push({
                key: ListManifestsParamKeys.TEAM_ID,
                label: 'Assigned to',
                value: teamId,
            });
        }

        if (createdBy) {
            list.push({
                key: ListManifestsParamKeys.CREATED_BY,
                label: 'Created by',
                value: createdBy,
            });
        }

        if (manifestStatuses?.length) {
            manifestStatuses.forEach((s) => {
                list.push({
                    key: ListManifestsParamKeys.MANIFEST_STATUSES,
                    label: 'Status',
                    value: s,
                });
            });
        }

        return list;
    }, [date, manifestStatuses, ownerId, teamId, createdBy]);

    const clearFilter = useCallback((key: string, value?: string) => {
        switch (key) {
            case ListManifestsParamKeys.QUERY: {
                setQueryInputVal('');
                break;
            }
            case ListManifestsParamKeys.PAGE: {
                setPage(0);
                break;
            }
            case ListManifestsParamKeys.EXACT_DATE: {
                setDate(undefined);
                break;
            }
            case ListManifestsParamKeys.OWNER_ID: {
                setOwnerId(undefined);
                break;
            }
            case ListManifestsParamKeys.TEAM_ID: {
                setTeamId(undefined);
                break;
            }
            case ListManifestsParamKeys.CREATED_BY: {
                setCreatedBy(undefined);
                break;
            }
            case ListManifestsParamKeys.MANIFEST_STATUSES: {
                if (value) {
                    setManifestStatuses((prev) => prev.filter((s) => s !== value));
                } else {
                    setManifestStatuses([]);
                }
                break;
            }
            case ListManifestsParamKeys.SORT: {
                setSortState({
                    sort: undefined,
                    sortDirection: undefined,
                });
                break;
            }
            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setPage(0);
        setArchived(undefined);
        setSortState({
            sort: initialSort,
            sortDirection: initialSortDir,
        });
        setDate(undefined);
        setQueryInputVal('');
        setOwnerId(undefined);
        setTeamId(undefined);
        setCreatedBy(undefined);
        setManifestStatuses([]);
    }, [initialSort, initialSortDir]);

    return {
        debouncedQuery,
        queryInputVal,
        appliedParams,
        tags,
        archived,
        setArchived,
        setPage,
        setSize,
        setSortState,
        clearFilter,
        clearAll,
        setQueryInputVal,
        setDate,
        setOwnerId,
        setTeamId,
        setCreatedBy,
        setManifestStatuses,
    };
};

export default useManifestsFilters;
